import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { UseMutationResult, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { usePortalMutation } from 'src/Libs/api'
import { StorageConfigDto, updateStorageConfig } from 'src/Libs/flow/installation/api'
import { getStorageConfigKey, useStorageConfig } from 'src/Libs/flow/installation/hooks'
import { EM360_DEFAULT_LIMIT_POWER_SETTINGS } from 'src/Libs/installations/constants'

export interface LimitPowerPayload {
  powerLimitsUsed: boolean
  emsMaxChargePower: number
  emsMaxDischargePower: number
  frequencyReverseProtectionLower: number
}

interface UseLimitPowerResult {
  query: UseQueryResult<Nullable<StorageConfigDto>, Error>
  mutation: UseMutationResult<null, Error, LimitPowerPayload>
}

export const useLimitPower = (installationId: number | undefined, deviceLinkId: string | null): UseLimitPowerResult => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  const query = useStorageConfig(installationId, deviceLinkId)

  const mutation = usePortalMutation<null, Error, LimitPowerPayload>({
    mutationFn: payload => {
      if (!installationId || !deviceLinkId) throw new Error('missing-ids')
      return updateStorageConfig(installationId, deviceLinkId, payload, authFetch)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getStorageConfigKey(installationId, deviceLinkId) })
    },
  })

  const { mutate } = mutation
  const hasTriggeredDefaultCreation = useRef(false)

  useEffect(() => {
    hasTriggeredDefaultCreation.current = false
  }, [installationId, deviceLinkId])

  useEffect(() => {
    if (!query.isSuccess || query.data !== null || hasTriggeredDefaultCreation.current) return

    hasTriggeredDefaultCreation.current = true
    mutate(EM360_DEFAULT_LIMIT_POWER_SETTINGS)
  }, [query.isSuccess, query.data, mutate])

  return { query, mutation }
}
